import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  AppCalculator,
  ApplicationTypes,
  BrokerageSelectionType,
  BrokerageSelectionValue,
  BusinessSearchResultValue,
  BusinessSearchValue,
  LoanTermType,
  LoanTermValue,
  PaymentFrequencyType,
  PaymentFrequencyValue,
  MIN_LOAN_AMOUNT_BUSINESS_LOAN_AND_OVERDRAFT,
  Quote,
  RatecardDetails,
  SearchCompanyByABNResult, SliderComponentValue,
  TotalPaymentBreakupDialogData,
  YesNoValue, calculateBusinessLoanEstimation,
  BUSINESS_LOAN_MAX_BROKERAGE_PERCENT,
} from '@portal-workspace/grow-shared-library';
import {
  ApplicationDialogService, BrokerageSelectionComponent, BusinessLoanTermsSelectionComponent, PortalHotToastService, doMarkAll,
  markTriggerSubject, setupUntilDestroy
} from '@portal-workspace/grow-ui-library';
import { Moment } from 'moment';
import numeral from 'numeral';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApplicationService } from '../../service/application.service';
import { navigationUrlForApplications } from '../../service/navigation-urls';

import { FlexModule } from '@angular/flex-layout/flex';
import { CurrencyInputComponent, MarkDirective, MessageBoxComponent, PaymentFrequencySelectionComponent, YesNoComponent } from '@portal-workspace/grow-ui-library';

// ZEN-818 follow-up: standalone TIB select maps to gstAgeMonths for the tier resolver.
// Term Loan requires >= 12 months TIB; the 12-23 band picks up the < 24 months
// ABNorGSTAge rate-card uplift (~2%) via the synthetic ABN/GST age below, so the
// separate ABN-age toggle is no longer needed.
export type TibSelectionValue = { id: '60+' | '24-59' | '12-23' | '<12'; name: string; gstAgeMonths: number; eligible: boolean } | null;
const TIB_SELECTION_OPTIONS: Exclude<TibSelectionValue, null>[] = [
  { id: '60+',   name: '5+ years (≥ 60 months)', gstAgeMonths: 60, eligible: true },
  { id: '24-59', name: '2–5 years (24–59 months)', gstAgeMonths: 24, eligible: true },
  { id: '12-23', name: '1–2 years (12–23 months)', gstAgeMonths: 12, eligible: true },
  { id: '<12',   name: 'Less than 1 year (< 12 months)', gstAgeMonths: 0, eligible: false },
];

const NOT_ELIGIBLE_MESSAGE = 'Customer is not eligible for a Dynamoney Term Loan';

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  templateUrl: './business-loans-calculator.page.html',
  styleUrls: ['business-loans-calculator.page.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FlexModule,
    MarkDirective,
    MatButtonModule,
    YesNoComponent,
    BrokerageSelectionComponent,
    PaymentFrequencySelectionComponent,
    BusinessLoanTermsSelectionComponent,
    CurrencyInputComponent,
    MatFormFieldModule,
    MatSelectModule,
    MessageBoxComponent,
],
})

export class BusinessLoansCalculatorPage implements OnInit {

  maxLimit: number | null = null;
  minLimit: number = MIN_LOAN_AMOUNT_BUSINESS_LOAN_AND_OVERDRAFT;
  BUSINESS_LOAN_MAX_BROKERAGE_PERCENT = BUSINESS_LOAN_MAX_BROKERAGE_PERCENT;

  subscriptions: Subscription[] = [];

  markTriggerSubject = markTriggerSubject;

  formGroupCalculator!: FormGroup<{
    loanTerms: FormControl<LoanTermValue>,
    loanAmount: FormControl<SliderComponentValue>,
    brokerage: FormControl<BrokerageSelectionValue>,
    paymentFrequency: FormControl<PaymentFrequencyValue>,
    propertyOwner: FormControl<YesNoValue>,
    adverseOnFile: FormControl<YesNoValue>,
    equifaxScore: FormControl<YesNoValue>,
    tib: FormControl<TibSelectionValue>,
  }>;

  formControlLoanAmount!: FormControl<SliderComponentValue>;
  formControlLoanTerms!: FormControl<LoanTermValue>;
  formControlBrokerage!: FormControl<BrokerageSelectionValue>;
  formControlPaymentFrequency!: FormControl<PaymentFrequencyValue>;
  formControlPropertyOwner!: FormControl<YesNoValue>;
  formControlAdverseOnFile!: FormControl<YesNoValue>;
  formControlEquifaxScoreAboveThreshold!: FormControl<YesNoValue>;
  formControlTib!: FormControl<TibSelectionValue>;
  tibOptions = TIB_SELECTION_OPTIONS;
  tibCompareFn = (a: TibSelectionValue, b: TibSelectionValue) => a?.id === b?.id;
  notEligibleMessage = NOT_ELIGIBLE_MESSAGE;

  get tibNotEligible(): boolean {
    const tib = this.formControlTib.value;
    return !!tib && !tib.eligible;
  }

  rateCard!: RatecardDetails;

  applicationType: ApplicationTypes = 'BusinessLoans';

  constructor(
    private formBuilder: FormBuilder,
    private toastService: PortalHotToastService,
    private applicationDialogService: ApplicationDialogService,
    private activatedRoute: ActivatedRoute,
    private appService: ApplicationService,
    private router: Router) {

    this.initCalculator();
  }

  ngOnInit() {
    setupUntilDestroy(this);
    this.rateCard = (this.activatedRoute.snapshot.data as any).ratecard;

    // update maxLimit limit
    this.subscriptions.push(this.formControlPropertyOwner.valueChanges.pipe(
      tap(r => {
        this.updateMaxLimit();
      })
    ).subscribe());

    this.subscriptions.push(this.formControlEquifaxScoreAboveThreshold.valueChanges.pipe(
      tap(() => {
        this.updateMaxLimit();
      })
    ).subscribe());

  }

  private updateMaxLimit() {
    const isPropertyOwner = this.formControlPropertyOwner.value;
    if (isPropertyOwner) {
      this.maxLimit = 500000;
    } else {
      this.maxLimit = 100000;
    }
  }

  initCalculator() {
    this.formControlLoanAmount = this.formBuilder.control(null, [Validators.required]);
    this.formControlLoanTerms = this.formBuilder.control(null, [Validators.required]);
    this.formControlBrokerage = this.formBuilder.control(null, [Validators.required]);
    this.formControlPaymentFrequency = this.formBuilder.control({ type: 'Weekly', name: 'Weekly' }, [Validators.required]);
    this.formControlPropertyOwner = this.formBuilder.control(true, [Validators.required]);
    this.formControlAdverseOnFile = this.formBuilder.control(false, [Validators.required]);
    this.formControlEquifaxScoreAboveThreshold = this.formBuilder.control(true, [Validators.required]);
    this.formControlTib = this.formBuilder.control(TIB_SELECTION_OPTIONS[0], [Validators.required]);

    this.formGroupCalculator = this.formBuilder.group({
      loanTerms: this.formControlLoanTerms,
      loanAmount: this.formControlLoanAmount,
      brokerage: this.formControlBrokerage,
      paymentFrequency: this.formControlPaymentFrequency,
      propertyOwner: this.formControlPropertyOwner,
      adverseOnFile: this.formControlAdverseOnFile,
      equifaxScore: this.formControlEquifaxScoreAboveThreshold,
      tib: this.formControlTib,
    });
  }

  onCalculateEstimation(event: Event) {
    markTriggerSubject(this.formGroupCalculator).next(true);
    doMarkAll(this.formGroupCalculator);
    if (this.tibNotEligible) {
      this.applicationDialogService.openAlertDialog({
        message: 'Not eligible',
        subMessage: NOT_ELIGIBLE_MESSAGE,
      });
    } else if (this.formGroupCalculator.invalid) {
      this.applicationDialogService.openAlertDialog({
        message: 'Missing Information',
        subMessage: 'Make sure all fields across tabs are filled in.',
      });
    } else {
      this.subscriptions.push(this.applicationDialogService.openAssetQuotationDialog({
        loadFn: (date) => {
          return this.calculateEstimation(date);
        },
        isQuote: true,
        paymentChartHideBalloonPayment: false,
        paymentChartHideBrokerOriginationFee: true,
        paymentChartHideBrokerage: false,
        showPaymentChart: true,
        showArmotizationChart: true,
        showRepaymentEstimation: true,
        showArmotisationSchedule: true,
      }).afterClosed()
        .pipe(
          tap(r => {
            if (r && r.acceptedQuotation) {
              const quoteData = this.saveQuoteData();
              this.appService.saveQuote(quoteData).subscribe(async res => {
                if (res.status) {
                  await this.router.navigate(navigationUrlForApplications())
                } else {
                  this.applicationDialogService.openAlertDialog({
                    message: `Error`,
                    // subMessage: " Couldn\'t save quote. Try again later."
                    subMessage: "Something is wrong. Try again."
                  });
                }
              })
            }
          })
        ).subscribe());
    }
  }

  private saveQuoteData(): Quote {
    // NOTE: formGroupCalculator will be valid when this is called, formControl values will not be null
    const loanCalValues = this.formGroupCalculator.getRawValue();
    const brokerageValue: BrokerageSelectionValue = this.formControlBrokerage.value;
    const loanAmount = this.formControlLoanAmount.value!;
    const quoteValue: Quote = {
      LoanAmount: (loanCalValues.loanAmount ?? '0').toString(),
      RepaymentFrequency: loanCalValues.paymentFrequency?.type!,
      LoanTerm: parseInt(loanCalValues.loanTerms?.type ?? '0'),
      BrokeragePercentage: parseFloat(loanCalValues.brokerage?.type ?? '0'),
      BrokerageAmount: new AppCalculator().calculateBrokerageAmount(this.formControlLoanAmount.value ?? 0, Number(brokerageValue?.type ?? '0')),
      QuoteAsJson: encodeURI(JSON.stringify(loanCalValues)),
    };
    return quoteValue
  }

  private calculateEstimation(date: Moment): TotalPaymentBreakupDialogData {
    // NOTE: formGroupCalculator will be valid when this is called, formControl values will not be null
    const paymentFrequencyValue: PaymentFrequencyValue = this.formControlPaymentFrequency.value;
    const paymentFrequencyType: PaymentFrequencyType = paymentFrequencyValue?.type ?? 'Monthly';
    const loanTermValue: LoanTermValue = this.formControlLoanTerms.value;
    const loanTermType: LoanTermType | null = loanTermValue?.type ?? null;
    const brokerageValue: BrokerageSelectionValue = this.formControlBrokerage.value;
    const brokerageType: BrokerageSelectionType = brokerageValue?.type ?? '0';
    const brokerage = Number(brokerageValue?.type ?? 0);
    const loanAmount = numeral(this.formControlLoanAmount.value).value() ?? 0;
    const businessSearchValue: BusinessSearchValue = this.createBusinessSearchValue();
    const bureauReport = businessSearchValue?.result!;
    const equifaxDirectorScoreAbove700 = this.formControlEquifaxScoreAboveThreshold.value ?? false;
    const adverseOnFile = this.formControlAdverseOnFile.value ?? false;
    const propertyOwner = this.formControlPropertyOwner.value ?? false;
    const gstAgeMonths = this.formControlTib.value?.gstAgeMonths ?? 60;

    const r = calculateBusinessLoanEstimation(date, {
      type: 'BusinessLoans',
      paymentFrequencyType,
      loanTermType,
      brokerageType,
      loanAmount,
      businessSearchValue,
      propertyOwner,
      brokerage,
      adverseOnFile,
      equifaxDirectorScoreAbove700,
      gstAgeMonths,
      rateCard: this.rateCard,
      bureauReport,
    });

    return r.totalPaymentBreakupDialogData;
  }

  createBusinessSearchValue(): BusinessSearchResultValue {
    // Synthetic ABN/GST age derived from the TIB selection: the 12-23 band falls in the
    // rate card's < 24 months ABNorGSTAge uplift band; 24+ attracts no uplift.
    const gstAgeMonths = this.formControlTib.value?.gstAgeMonths ?? 60;
    return {
      type: 'search-result',
      result: {
        ABNAgeMonths: gstAgeMonths,
        GSTAgeMonths: gstAgeMonths
      } as SearchCompanyByABNResult
    } as BusinessSearchResultValue;
  }
}
